import type { Config } from "wagmi"
import { readContract } from "@wagmi/core"

import { LcAddress } from "@/domains/core"

import type { RawBribeData } from "./types"
import { infoBribeByNftAbi } from "./abi/info-bribe-by-nft.abi"

const loggedRewardFailures = new Set<string>()

export type LockRewardsOnChainTransportAddresses = {
  infoBribeByNft: LcAddress
}

export class LockRewardsOnChainTransport {
  constructor(
    private config: Config,
    private readonly addresses: LockRewardsOnChainTransportAddresses,
  ) {}

  /**
   * Load rewards for a specific NFT ID from on-chain
   * Pure data loading - no state, no caching, no side effects.
   *
   * Wagmi auto-batches neighbouring `readContract` calls through
   * Multicall3. `infoBribeByNftId` is heavy enough that 4–6 calls batched
   * together regularly trip the RPC's per-request gas cap and come back as
   * `out of gas`. We swallow the failure and return `[]` — the orchestrator
   * simply retries on the next tick — and use `console.debug` so the error
   * does not drown out genuine failures in the console. If this becomes a
   * persistent issue we should either lower the wagmi batch size for this
   * call site or override `gas` on the underlying `eth_call` directly.
   */
  async loadRewardsByNftId(nftId: number): Promise<RawBribeData[]> {
    try {
      const result = await readContract(this.config, {
        address: this.addresses.infoBribeByNft.toViemAddress(),
        abi: infoBribeByNftAbi,
        functionName: "infoBribeByNftId",
        args: [BigInt(nftId)],
      })

      return result as RawBribeData[]
    } catch (error) {
      const message = error instanceof Error ? error.message.split("\n")[0] : String(error)
      const logKey = `${nftId}:${message}`
      if (
        import.meta.env.DEV &&
        globalThis.localStorage?.getItem("debug-lock-rewards") === "true" &&
        !loggedRewardFailures.has(logKey)
      ) {
        loggedRewardFailures.add(logKey)
        console.debug(`[lock-rewards] rewards unavailable for NFT ${nftId}: ${message}`)
      }
      return []
    }
  }
}
